class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    if (
      window.location.pathname === '/cart' ||
      new URLSearchParams(window.location.search).get('open_cart') === 'true'
    ) {
      setTimeout(() => {
        this.open();
      }, 100);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      if (typeof initializeUpsellsSwiper === 'function') {
        initializeUpsellsSwiper();
      }
      if (typeof initializeChooseSizeComponents === 'function') {
        initializeChooseSizeComponents();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

let upsellsSlider = null;

function initializeUpsellsSwiper() {
  const upsellsSliderContainer = document.querySelector('.cart-upsells__swiper');
  if (!upsellsSliderContainer) return;

  if (upsellsSlider && typeof upsellsSlider.destroy === 'function') {
    upsellsSlider.destroy(true, true);
  }

  upsellsSlider = new Swiper(upsellsSliderContainer, {
    slidesPerView: 'auto',
    spaceBetween: 16,
    centeredSlides: false,
    loop: false,
    navigation: {
      nextEl: '.slider-button--next',
      prevEl: '.slider-button--prev',
    },
    speed: 400,
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeUpsellsSwiper);
} else {
  initializeUpsellsSwiper();
}

function initializeChooseSizeComponents() {
  document.querySelectorAll('choose-size').forEach(element => {
    if (typeof element.connectedCallback === 'function') {
      element.connectedCallback();
    }
  });
}

class CartUpsellAdd extends HTMLElement {
  connectedCallback() {
    this.addButton = this.querySelector('.cart-upsell-item__add');
    this.variantId = this.addButton.getAttribute('data-variant-id');
    this.spinner = this.querySelector('.loading__spinner');

    if (!this.variantId || !this.addButton) return;

    this.addButton.addEventListener('click', (event) => {
      event.preventDefault();
      this.startLoading();

      fetch(`${routes.cart_add_url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: this.variantId, quantity: 1 })
      })
        .then(() => {
          return fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`);
        })
        .then((res) => res.json())
        .then((sections) => {
          document.querySelector('cart-drawer')?.renderContents({
            id: this.variantId,
            sections
          });
        })
        .catch((error) => {
          console.error('Error updating cart:', error);
        })
        .finally(() => {
          this.finishLoading();
        });
    });
  }

  startLoading() {
    this.addButton?.classList.add('loading');
    console.log('[CartUpsellItem] Spinner before enabling:', this.spinner?.classList);
    this.spinner?.classList.remove('hidden');
    console.log('[CartUpsellItem] Spinner after enabling:', this.spinner?.classList);
  }

  finishLoading() {
    this.addButton?.classList.remove('loading');
    console.log('[CartUpsellItem] Spinner before disabling:', this.spinner?.classList);
    this.spinner?.classList.add('hidden');
    console.log('[CartUpsellItem] Spinner after disabling:', this.spinner?.classList);
  }
}

customElements.define('cart-upsell-add', CartUpsellAdd);